import React, { useState } from 'react';
import { Flame, Send } from 'lucide-react';

const CoalFiredContact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    facilityLocation: '',
    projectType: '',
    coalSource: '',
    boilerType: '',
    fuelCapacity: '',
    currentEmissions: '',
    emissionControls: '',
    coalGrade: '',
    ashHandling: '',
    waterCooling: '',
    stackHeight: '',
    nearbyReceptors: '',
    complianceHistory: '',
    modernizationPlans: '',
    timeline: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const projectTypes = [
    { value: '', label: 'Select project type' },
    { value: 'new-facility', label: 'New Coal-Fired Facility' },
    { value: 'boiler-replacement', label: 'Boiler Replacement' },
    { value: 'capacity-increase', label: 'Capacity Increase' },
    { value: 'emission-upgrade', label: 'Emission Control Upgrade' },
    { value: 'fuel-switch', label: 'Fuel Switching (Coal to Other)' },
    { value: 'permit-renewal', label: 'Permit Renewal/Modification' },
    { value: 'compliance', label: 'Compliance Assessment' }
  ];

  const coalGrades = [
    { value: '', label: 'Select coal grade' },
    { value: 'anthracite', label: 'Anthracite (High Grade)' },
    { value: 'bituminous', label: 'Bituminous (Medium Grade)' },
    { value: 'sub-bituminous', label: 'Sub-Bituminous' },
    { value: 'lignite', label: 'Lignite (Low Grade)' },
    { value: 'mixed', label: 'Mixed/Variable' },
    { value: 'unknown', label: 'Unknown/TBD' }
  ];

  const boilerTypes = [
    { value: '', label: 'Select boiler type' },
    { value: 'pulverized-coal', label: 'Pulverized Coal Boiler' },
    { value: 'stoker', label: 'Stoker-Fired Boiler' },
    { value: 'fluidized-bed', label: 'Fluidized Bed Combustion' },
    { value: 'cyclone', label: 'Cyclone Furnace' },
    { value: 'other', label: 'Other Type' }
  ];

  const emissionControlOptions = [
    { value: '', label: 'Current/planned emission controls' },
    { value: 'esp', label: 'Electrostatic Precipitator (ESP)' },
    { value: 'baghouse', label: 'Baghouse/Fabric Filter' },
    { value: 'fgd', label: 'Flue Gas Desulfurization (FGD/Scrubber)' },
    { value: 'scr', label: 'Selective Catalytic Reduction (SCR)' },
    { value: 'sncr', label: 'Selective Non-Catalytic Reduction (SNCR)' },
    { value: 'multiple', label: 'Multiple Control Systems' },
    { value: 'none', label: 'None Currently' },
    { value: 'planning', label: 'Planning/Evaluating Options' }
  ];

  const timelines = [
    { value: '', label: 'Select project timeline' },
    { value: 'immediate', label: 'Immediate (Within 3 months)' },
    { value: '6-months', label: '3-6 Months' },
    { value: '12-months', label: '6-12 Months' },
    { value: '1-2-years', label: '1-2 Years' },
    { value: '2-plus-years', label: 'More than 2 Years' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/coal-fired`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your coal-fired power plant inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityLocation: '', projectType: '', coalSource: '', boilerType: '',
          fuelCapacity: '', currentEmissions: '', emissionControls: '', coalGrade: '',
          ashHandling: '', waterCooling: '', stackHeight: '', nearbyReceptors: '',
          complianceHistory: '', modernizationPlans: '', timeline: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-700 text-white p-8 rounded-t-xl">
          <div className="flex items-center mb-4">
            <Flame className="w-12 h-12 mr-4" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Coal-Fired Power Plants</h1>
              <p className="text-gray-200 mt-2">Specialized Environmental Permitting & Compliance</p>
            </div>
          </div>
          <p className="text-gray-300 mt-4">
            Alaska's coal-fired power plants require expert environmental permitting to balance energy needs 
            with stringent air quality standards. Share your project details for tailored consulting services.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl">
          {/* Contact Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company/Organization *</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Facility Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Facility Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Facility Location</label>
                <input
                  type="text"
                  name="facilityLocation"
                  value={formData.facilityLocation}
                  onChange={handleChange}
                  placeholder="City/Region in Alaska"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  {projectTypes.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coal Grade</label>
                <select
                  name="coalGrade"
                  value={formData.coalGrade}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  {coalGrades.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Coal Source Location</label>
                <input
                  type="text"
                  name="coalSource"
                  value={formData.coalSource}
                  onChange={handleChange}
                  placeholder="e.g., Local Alaska mine, Imported"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Boiler Type</label>
                <select
                  name="boilerType"
                  value={formData.boilerType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  {boilerTypes.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fuel Capacity (MMBtu/hr or MW)</label>
                <input
                  type="text"
                  name="fuelCapacity"
                  value={formData.fuelCapacity}
                  onChange={handleChange}
                  placeholder="e.g., 250 MMBtu/hr or 50 MW"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Current/Expected Emissions</label>
              <textarea
                name="currentEmissions"
                value={formData.currentEmissions}
                onChange={handleChange}
                rows="2"
                placeholder="Known or estimated emissions (SOx, NOx, PM, CO2, Mercury)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Emission Controls</label>
                <select
                  name="emissionControls"
                  value={formData.emissionControls}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                >
                  {emissionControlOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stack Height (feet)</label>
                <input
                  type="text"
                  name="stackHeight"
                  value={formData.stackHeight}
                  onChange={handleChange}
                  placeholder="e.g., 200 feet"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ash Handling Method</label>
                <input
                  type="text"
                  name="ashHandling"
                  value={formData.ashHandling}
                  onChange={handleChange}
                  placeholder="e.g., Dry collection, Wet disposal"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cooling Water System</label>
                <input
                  type="text"
                  name="waterCooling"
                  value={formData.waterCooling}
                  onChange={handleChange}
                  placeholder="e.g., Once-through, Cooling tower"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Environmental Considerations */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Environmental & Regulatory</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Nearby Sensitive Receptors</label>
              <textarea
                name="nearbyReceptors"
                value={formData.nearbyReceptors}
                onChange={handleChange}
                rows="2"
                placeholder="Residential areas, schools, hospitals, protected areas within 5 miles"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Compliance History</label>
              <textarea
                name="complianceHistory"
                value={formData.complianceHistory}
                onChange={handleChange}
                rows="2"
                placeholder="Any past violations, notices, or compliance issues"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Modernization/Upgrade Plans</label>
              <textarea
                name="modernizationPlans"
                value={formData.modernizationPlans}
                onChange={handleChange}
                rows="2"
                placeholder="Planned improvements, retrofits, or efficiency upgrades"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
          </div>

          {/* Project Timeline */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Timeline</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              >
                {timelines.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Requirements or Concerns <span className="text-red-500">*</span>
              </label>
              <textarea
                name="specificRequirements"
                value={formData.specificRequirements}
                onChange={handleChange}
                required
                rows="4"
                placeholder="e.g., NSPS Subpart DDDDD compliance, HAP emission concerns, mercury control needs, CEMS requirements, MACT standards, permit modification needs, environmental impact concerns, etc."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows="4"
                placeholder="Any other details about your coal-fired power plant project, specific concerns, or questions"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              ></textarea>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center">
            <button
              type="submit"
              className="inline-flex items-center px-8 py-4 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors shadow-lg"
            >
              Submit Coal-Fired Plant Inquiry
              <Send className="ml-2" size={20} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CoalFiredContact;
